import LoginLayout from '@/layout/LoginLayout';
import { trpc } from '@/trpcClient';
import { DefinePasswordInputType, checkDefinePasswordInput } from '@shelflix/schemas/authentication/definePassword';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDefinePasswordFormStore } from './store';
import { extractTenantSlug, getTenantDomain } from '@/utils/tenantDetection';

export default function LoginDefinePassword() {
  const { formData, resetForm } = useDefinePasswordFormStore();
  const definePasswordMutation = useMutation(trpc.authentication['define-password'].mutationOptions());
  const [searchParams] = useSearchParams();
  const isFirstAccess = searchParams.has('firstAccess');
  const text = isFirstAccess ? 'Defina' : 'Redefina';
  const navigate = useNavigate();

  const form = useForm<DefinePasswordInputType & { confirmPassword: string }>({
    defaultValues: {
      ...formData,
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = (data: DefinePasswordInputType) => {
    if (definePasswordMutation.isPending) return;

    // Get tenant slug from current domain for multi-tenant isolation
    const tenantSlug = extractTenantSlug(getTenantDomain());

    const payload = {
      password: data.password,
      email: localStorage.getItem('email'),
      code: localStorage.getItem('code'),
      tenantSlug,
    };

    definePasswordMutation.mutate(payload, {
      onSuccess: async (response) => {
        localStorage.setItem('authToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.removeItem('email');
        localStorage.removeItem('code');
        navigate('/pos');
        resetForm();
      },
      onError: (error) => {
        const isBadRequest = error.data?.httpStatus === 400;
        if (isBadRequest) {
          toast.error('Dados inválidos. Volte para a tela de login e clique em primeiro acesso.');
        } else {
          toast.error(`${error.message}`);
        }
      },
    });
  };

  return (
    <LoginLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex flex-col gap-4">
          <div className="mb-4 text-center">
            <div className="mx-auto max-w-xs text-sm text-gray-700">
              {text} sua senha abaixo, utilize ao menos um caractere especial, um maiúsculo e um numérico.
            </div>
          </div>
          <FormField
            control={form.control}
            name="password"
            rules={{ validate: (value) => checkDefinePasswordInput('password', value) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Senha</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <Input type="password" id="password" placeholder="Defina a sua senha" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            rules={{
              validate: (value) => value === form.getValues('password') || 'As duas senhas devem ser iguais',
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Confirmar Senha</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <Input type="password" id="confirmPassword" placeholder="Repita a senha informada" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="mt-4 w-full bg-[var(--primary)]" loading={definePasswordMutation.isPending}>
            {definePasswordMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Carregando...</span>
              </div>
            ) : (
              <>
                <Icon name="login" className="mr-2" />
                Login
              </>
            )}
          </Button>
        </form>
      </Form>
    </LoginLayout>
  );
}
